import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { StatsApi, type MeasureTotals, type StatsQuery } from '@/services/stats/statsApi'
import { getDateRange, type DateRange } from '@/services/stats/statsRange'
import { AuthService } from '@/services/core/auth'
import { useNetwork } from '../contexts/NetworkContext'
import { enumLabel } from '../utils/enumLabel'

/** Which endpoint the measures come from. Both answer with the same shape. */
export type MeasureKind = 'traffic' | 'location'

/** One value inside a measure — a device type, an OS, a country. */
export interface MeasureRow {
  /** The value as the ad server names it, e.g. `MOBILE`. */
  key: string
  label: string
  value: number
  /** The value's share of its own measure, 0-100. */
  share: number
}

/** One measure and everything counted under it. */
export interface MeasureGroup {
  /** Response field the measure came from, e.g. `deviceType`. */
  key: string
  rows: MeasureRow[]
  total: number
}

const FETCHERS: Record<MeasureKind, (query: StatsQuery) => Promise<MeasureTotals>> = {
  traffic: StatsApi.getTraffic,
  location: StatsApi.getLocation,
}

/**
 * Countries come back as display names already; everything else is an enum
 * name that needs reading. Keeping this per measure avoids re-casing a country
 * whose name happens to be upper-case.
 */
const VERBATIM_MEASURES = new Set(['country', 'city'])

/**
 * Delivery counted across a set of measures — devices for `traffic`, countries
 * for `location`. Measures come back in the order `measures` names them so the
 * table reads the same every time, and empty ones are dropped.
 */
export function useMeasureStats(
  kind: MeasureKind,
  /** Response fields to show, in order. */
  measures: string[],
  query: Partial<StatsQuery>,
  range: DateRange,
  enabled = true
) {
  const { currentNetwork } = useNetwork()
  const timeZone = currentNetwork?.timeZone ?? 'UTC'
  const hasToken = AuthService.isAuthenticated()

  const { startDate, endDate } = useMemo(() => getDateRange(range, timeZone), [range, timeZone])

  const result = useQuery({
    queryKey: ['measure-stats', kind, query, range, startDate, endDate],
    queryFn: () =>
      FETCHERS[kind]({
        startDate,
        endDate,
        // No groupBy: the endpoints then answer with the period's totals per
        // measure, which is one row per table row.
        timeout: 180000,
        ...query,
      }),
    enabled: enabled && hasToken && !!currentNetwork,
  })

  const groups = useMemo((): MeasureGroup[] => {
    const data = result.data ?? {}
    return measures.flatMap((measure) => {
      const counts = data[measure]
      if (!counts) return []
      const total = Object.values(counts).reduce((sum, count) => sum + count, 0)
      const rows = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => ({
          key,
          label: VERBATIM_MEASURES.has(measure) ? key : enumLabel(key),
          value: count,
          share: total > 0 ? (count / total) * 100 : 0,
        }))
        .sort((a, b) => b.value - a.value)
      return rows.length ? [{ key: measure, rows, total }] : []
    })
  }, [result.data, measures])

  return {
    groups,
    isLoading: result.isLoading,
    error: result.error as Error | null,
  }
}
